import React, { useMemo } from 'react';
import { AdvancedAIEngine } from '../engines/AdvancedAIEngine.js';

function AIInsightsTabV2({ expirations }) {
  const insights = useMemo(() => {
    const engine = new AdvancedAIEngine(expirations);
    return engine.generateTradingInsights();
  }, [expirations]);

  return (
    <div className="ai-insights-tab-v2">
      <div className="insights-header">
        <h2>🤖 תובנות AI למסחר</h2>
        <p className="insights-subtitle">ניתוח מתקדם לקבלת החלטות מושכלות</p>
      </div>

      {/* Alerts */}
      {insights.alerts.length > 0 && (
        <div className="alerts-section">
          {insights.alerts.map((alert, index) => (
            <div key={index} className={`alert-card ${alert.level}`}>
              <div className="alert-icon">{alert.icon}</div>
              <div className="alert-content">
                <div className="alert-message">{alert.message}</div>
                <div className="alert-action">{alert.action}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Strategy Recommendation */}
      <div className="strategy-recommendation-section">
        <h3>💡 המלצת אסטרטגיה - עכשיו!</h3>
        <div className="strategy-main-card">
          <div className="strategy-header-section">
            <div className="strategy-name">
              <span className="strategy-icon">
                {insights.strategyRecommendation.category === 'שחיקה (Theta Decay)' ? '🐌' : '🚀'}
              </span>
              <div>
                <h4>{insights.strategyRecommendation.primary}</h4>
                <span className={`strategy-category ${
                  insights.strategyRecommendation.category === 'שחיקה (Theta Decay)' ? 'theta' : 'directional'
                }`}>
                  {insights.strategyRecommendation.category}
                </span>
              </div>
            </div>
            <div className={`confidence-badge ${insights.strategyRecommendation.confidence}`}>
              {insights.strategyRecommendation.confidence}
            </div>
          </div>

          <div className="strategy-reason">
            <strong>סיבה:</strong> {insights.strategyRecommendation.reason}
          </div>

          {insights.strategyRecommendation.warning && (
            <div className="strategy-warning">
              {insights.strategyRecommendation.warning}
            </div>
          )}

          <div className="strategy-details-grid">
            <div className="detail-item">
              <span className="detail-label">שיעור הצלחה צפוי:</span>
              <span className="detail-value">{insights.strategyRecommendation.expectedWinRate}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">רמת ביטחון:</span>
              <span className="detail-value">{insights.strategyRecommendation.confidence}</span>
            </div>
          </div>

          <div className="strikes-section">
            <h5>💰 Strikes מומלצים:</h5>
            <div className="strikes-grid">
              {Object.entries(insights.strategyRecommendation.strikes).map(([key, value]) => (
                <div key={key} className="strike-item">
                  <span className="strike-label">{key}:</span>
                  <span className="strike-value">{value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Probability Ranges */}
      <div className="probability-section">
        <h3>🎲 הסתברויות תנועה</h3>
        <p className="section-description">סיכוי למהלך מעבר לטווח - עזרה בבחירת strikes</p>
        <div className="probability-grid">
          {insights.probabilityRanges.map((range, index) => (
            <div key={index} className="probability-card">
              <div className="prob-range">{range.range}</div>
              <div className="prob-percentage">{range.probability}%</div>
              <div className="prob-count">{range.count} מתוך {range.total}</div>
              <div className={`prob-suitability ${
                range.ironCondorSuitable === 'מצוין' ? 'excellent' :
                range.ironCondorSuitable === 'טוב' ? 'good' : 'risky'
              }`}>
                Iron Condor: {range.ironCondorSuitable}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Day of Week Patterns */}
      <div className="day-patterns-section">
        <h3>📅 דפוסים לפי יום - ראשון מול ראשון, שלישי מול שלישי</h3>
        <p className="section-description">
          השוואה של אותו יום לעומת הפעם הקודמת שהיה אותו יום
        </p>
        <div className="day-patterns-grid">
          {Object.entries(insights.dayOfWeekPatterns).map(([day, stats]) => (
            <div key={day} className="day-pattern-card">
              <div className="day-header">
                <h4>{day}</h4>
                <span className="day-count">({stats.count} פקיעות)</span>
              </div>

              <div className="day-stats">
                <div className="stat-row">
                  <span>ממוצע שינוי:</span>
                  <strong className={parseFloat(stats.avgChange) >= 0 ? 'positive' : 'negative'}>
                    {stats.avgChange >= 0 ? '+' : ''}{stats.avgChange}%
                  </strong>
                </div>
                <div className="stat-row">
                  <span>סטיית תקן:</span>
                  <strong>{stats.stdDev}%</strong>
                </div>
                <div className="stat-row">
                  <span>אחוז רווח:</span>
                  <strong>{stats.positiveRate}%</strong>
                </div>
                <div className="stat-row">
                  <span>טווח:</span>
                  <strong className="negative">{stats.minChange}%</strong>
                  {' עד '}
                  <strong className="positive">+{stats.maxChange}%</strong>
                </div>
              </div>

              <div className="day-recommendation">
                <strong>המלצה:</strong>
                <p>{stats.recommendation}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Seasonality Advanced */}
      <div className="seasonality-advanced-section">
        <h3>📊 עונתיות מתקדמת - לפי חודש ושנה</h3>
        <p className="section-description">
          שים לב לחודשים ספציפיים שבהם היו תנועות חריגות
        </p>

        {/* Monthly Patterns */}
        <div className="monthly-patterns">
          <h4>דפוסים לפי חודש:</h4>
          <div className="monthly-patterns-grid">
            {Object.entries(insights.seasonality.monthlyPatterns).map(([month, pattern]) => (
              <div key={month} className="monthly-pattern-card">
                <div className="month-name">{month}</div>
                <div className={`month-avg ${parseFloat(pattern.avgChange) >= 0 ? 'positive' : 'negative'}`}>
                  {pattern.avgChange >= 0 ? '+' : ''}{pattern.avgChange}%
                </div>
                <div className="month-pattern">{pattern.pattern}</div>
                {pattern.negativeYears.length > 0 && (
                  <div className="years-list negative">
                    ⚠️ שלילי ב: {pattern.negativeYears.join(', ')}
                  </div>
                )}
                {pattern.positiveYears.length > 0 && pattern.positiveYears.length <= 3 && (
                  <div className="years-list positive">
                    ✅ חיובי ב: {pattern.positiveYears.join(', ')}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Year-Month Breakdown */}
        <div className="year-month-breakdown">
          <h4>פירוט מלא לפי שנה:</h4>
          <div className="breakdown-table">
            <table>
              <thead>
                <tr>
                  <th>תקופה</th>
                  <th>ממוצע</th>
                  <th>אחוז רווח</th>
                  <th>מגמה</th>
                  <th>חשיבות</th>
                </tr>
              </thead>
              <tbody>
                {insights.seasonality.byMonthYear.slice(0, 24).map((item, index) => (
                  <tr key={index} className={item.significance === 'גבוהה' ? 'significant' : ''}>
                    <td><strong>{item.period}</strong></td>
                    <td className={parseFloat(item.avgChange) >= 0 ? 'positive' : 'negative'}>
                      {item.avgChange >= 0 ? '+' : ''}{item.avgChange}%
                    </td>
                    <td>{item.winRate}%</td>
                    <td>{item.trend}</td>
                    <td>{item.significance === 'גבוהה' ? '⚠️ גבוהה' : 'רגילה'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Market State */}
      <div className="market-state-section">
        <h3>📈 מצב שוק נוכחי</h3>
        <div className="market-state-grid">
          <div className="state-card">
            <h4>מגמה אחרונה</h4>
            <div className="state-value">
              <span className={`trend-icon ${insights.recentTrend.direction}`}>
                {insights.recentTrend.direction === 'עולה' ? '📈' :
                 insights.recentTrend.direction === 'יורד' ? '📉' : '↔️'}
              </span>
              <span>{insights.recentTrend.direction}</span>
            </div>
            <div className="state-details">
              <div>עוצמה: {insights.recentTrend.strength}</div>
              <div>עקביות: {insights.recentTrend.consistency}</div>
              <div>ממוצע: {insights.recentTrend.avgChange}%</div>
            </div>
          </div>

          <div className="state-card">
            <h4>תנודתיות</h4>
            <div className="state-value">
              <span className={`vol-badge ${insights.volatilityState.state}`}>
                {insights.volatilityState.state}
              </span>
            </div>
            <div className="state-details">
              <div>נוכחי: {insights.volatilityState.current}%</div>
              <div>היסטורי: {insights.volatilityState.historical}%</div>
            </div>
            <div className="state-recommendation">
              {insights.volatilityState.recommendation}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AIInsightsTabV2;
